import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Modal } from 'react-bootstrap';
import axios from 'axios';
import GLOBALVAR from '../../constants';

// Update component props to include modal props
const AddTemplate = ({ template, show, onHide, onSuccess, showNotification }) => {
    const [formData, setFormData] = useState({
        name: '',
        type: 'email',
        category: '',
        subject: '',
        body: '',
        variables: {}, // Make sure this is an object, not a string
        isActive: true,
        senderId: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Initialize form data with template if editing
    useEffect(() => {
        if (template) {
            setFormData(template);
        }
    }, [template]);

    // Update handleSubmit to use onSuccess callback
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        try {
            // Parse the variables field if it's a string
            const dataToSubmit = {
                ...formData,
                variables: typeof formData.variables === 'string' 
                    ? JSON.parse(formData.variables) 
                    : formData.variables
            };

            const endpoint = template 
                ? `${GLOBALVAR['SERVER_ROOT']}/api/templates/${template.id}`
                : `${GLOBALVAR['SERVER_ROOT']}/api/templates`;
            
            const method = template ? 'put' : 'post';
            
            await axios[method](endpoint, dataToSubmit);
            
            showNotification(`Template ${template ? 'updated' : 'created'} successfully`);
            if (onSuccess) {
                onSuccess();
            }
            if (!template) {
                resetForm();
            }
        } catch (error) {
            console.log(error);
            if (error.response?.data?.errors) {
                const errorObj = {};
                error.response.data.errors.forEach(err => {
                    errorObj[err.param] = err.msg;
                });
                setErrors(errorObj);
            }
            showNotification(error.response?.data?.message || 'Failed to save template', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // Add reset form function
    const resetForm = () => {
        setFormData({
            name: '',
            type: 'email',
            category: '',
            subject: '',
            body: '',
            variables: {}, // Make sure this is an object, not a string
            isActive: true,
            senderId: ''
        });
    };

    // Wrap the form in a Modal if show prop is provided
    const content = (
        <Card>
            <Card.Body>
                <Card.Title>{template ? 'Edit Template' : 'Add New Template'}</Card.Title>
                <Form onSubmit={handleSubmit}>
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Template Name*</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    isInvalid={!!errors.name}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.name}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Type*</Form.Label>
                                <Form.Select
                                    value={formData.type}
                                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                                    isInvalid={!!errors.type}
                                >
                                    <option value="email">Email</option>
                                    <option value="sms">SMS</option>
                                    <option value="both">Both</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.type}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Category*</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.category}
                                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                                    isInvalid={!!errors.category}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.category}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        {(formData.type === 'email' || formData.type === 'both') && (
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>Subject</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.subject}
                                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                                        isInvalid={!!errors.subject}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.subject}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        )}

                        <Col md={12}>
                            <Form.Group>
                                <Form.Label>Body*</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={6}
                                    value={formData.body}
                                    onChange={(e) => setFormData({...formData, body: e.target.value})}
                                    isInvalid={!!errors.body}
                                />
                                <Form.Text className="text-muted">
                                    Use {'{{'}variableName{'}}'} syntax for dynamic content
                                </Form.Text>
                                <Form.Control.Feedback type="invalid">
                                    {errors.body}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <div className="d-flex justify-content-end gap-2">
                                <Button 
                                    variant="primary" 
                                    type="submit"
                                    disabled={loading}
                                >
                                    {loading ? 'Saving...' : template ? 'Update Template' : 'Create Template'}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Card.Body>
        </Card>
    );

    return show ? (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{template ? 'Edit Template' : 'Add New Template'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {content}
            </Modal.Body>
        </Modal>
    ) : content;
};

export default AddTemplate;